import type { QuizBuilderSectionSchema } from '@repo/schemas/quiz/quizBuilder';

import { arrayMove } from '@dnd-kit/helpers';
import { DragDropProvider, DragOverlay, PointerSensor } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { hms } from '@repo/lib/time/timeUtils';
import { DotIcon, GripVerticalIcon } from 'lucide-react';
import { useState } from 'react';

import QuestionsSidebarHeader from '@/components/features/quiz/header/QuestionsSidebarHeader';
import QuizBuilderSectionAppendPopover from '@/components/features/quiz/quiz-builder/QuizBuilderSectionAppendPopover';
import QuizBuilderSectionCard from '@/components/features/quiz/quiz-builder/QuizBuilderSectionCard';
import { SortableItem } from '@/components/ui/dnd';
import { useQuizBuilder } from '@/context/quiz-builder';

export default function QuizBuilderContentPage() {
  const { totalQuestions, removeSection, appendQuestion, duplicateQuestion, duplicateSection, removeQuestion, saveDraftData, sectionsForm, sections } = useQuizBuilder();

  const [activeSection, setActiveSection] = useState<QuizBuilderSectionSchema | null>(null);

  return (
    <div className="grow overflow-auto">
      <div className="flex grow flex-col p-4">
        <div className="space-y-3">
          <QuestionsSidebarHeader number={totalQuestions}>
            <QuizBuilderSectionAppendPopover />
          </QuestionsSidebarHeader>
          <div className="space-y-3">
            <DragDropProvider
              sensors={[PointerSensor]}
              onDragStart={(event) => {
                const { source } = event.operation;
                if (source?.type === 'section' && source?.data?.index !== undefined) {
                  const sectionIndex = source.data.index as number;
                  setActiveSection(sections[sectionIndex] || null);
                }
              }}
              onDragEnd={(event) => {
                setActiveSection(null);
                const { target, source } = event.operation;
                const { canceled } = event;

                if (canceled) return;
                if (!target || !source) return;
                if (!isSortable(source)) return;

                if (source.type == 'section') {
                  const oldIndex: number = source.data.index;
                  const newIndex: number = source.sortable.index;

                  const reordered = arrayMove(sections, oldIndex, newIndex);
                  sectionsForm.setValue('content', reordered);
                  saveDraftData();
                } else if (source.type == 'question') {
                  const isDropBox = target.id == 'question-box';

                  const oldIndex: number = source.data.index;
                  const oldSection: number = source.data.sectionIndex;
                  const newIndex: number = !isDropBox ? source.sortable.index : target.data.index;
                  const newSection: number = !isDropBox ? (source.sortable.group as number) : target.data.sectionIndex;

                  const qData = source.data.question;

                  const updated = [...sections];

                  //delete from old section
                  if (updated[oldSection] && Array.isArray(updated[oldSection].questions)) {
                    updated[oldSection].questions = updated[oldSection].questions.filter((_, i) => i !== oldIndex);
                  }

                  //move to new section
                  if (updated[newSection] && Array.isArray(updated[newSection].questions)) {
                    updated[newSection].questions.push(qData);

                    if (updated.length > 1) {
                      updated[newSection].questions = arrayMove(updated[newSection].questions, updated[newSection].questions.length - 1, newIndex);
                    }
                  }

                  sectionsForm.setValue(`content`, updated);
                  saveDraftData();
                }
              }}
            >
              <div className="space-y-4 pt-4">
                {sections.length === 0 && (
                  <div className={'text-text-tertiary bg-surface-secondary/75 border-border-destructive/50 rounded-md border border-dashed px-3 py-6 text-center text-sm'}>
                    <span>Brak sekcji. Dodaj nową sekcję, aby rozpocząć budowanie quizu.</span>
                  </div>
                )}
                {sections.map((section, index) => (
                  <SortableItem
                    key={section.id}
                    id={section.id}
                    index={index}
                    data={{ index }}
                    args={{
                      type: 'section',
                      accept: 'section',
                    }}
                  >
                    <QuizBuilderSectionCard
                      key={index}
                      sectionIndex={index}
                      section={section}
                      removeSection={removeSection}
                      appendQuestion={appendQuestion}
                      removeQuestion={removeQuestion}
                      duplicateQuestion={duplicateQuestion}
                      duplicateSection={duplicateSection}
                      sectionsForm={sectionsForm}
                    />
                  </SortableItem>
                ))}
              </div>
              <DragOverlay>
                {() => {
                  if (!activeSection) return null;

                  return (
                    <div className="border-border-primary bg-surface-primary pointer-events-none flex items-center gap-2 rounded-md border p-2 shadow-lg">
                      <GripVerticalIcon className="text-text-secondary size-4 shrink-0" />
                      <span className="text-text-primary truncate text-sm font-medium">{activeSection.name}</span>
                      <DotIcon className="text-text-secondary size-4 shrink-0" />
                      {activeSection.time ? (
                        <span className="text-text-secondary truncate text-xs font-medium">{hms(activeSection.time)}</span>
                      ) : (
                        <span className="text-text-tertiary text-xs">Brak limitu czasu</span>
                      )}
                    </div>
                  );
                }}
              </DragOverlay>
            </DragDropProvider>
          </div>
        </div>
      </div>
    </div>
  );
}
